import React, { createContext, useContext, useEffect, useState } from 'react';
import axiosInstance from '../services/axiosInstance';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';

// Décode le JWT
const parseJwt = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [authChecked, setAuthChecked] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    const logout = (redirect = true) => {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setIsLoggedIn(false);
        setUser(null);
        setAuthChecked(true);

        if (redirect) {
            navigate('/');
        }
    };


    const login = async (email, password) => {
        try {
            const response = await api.post('/frontoffice/login_check', {
                email,
                password,
                tokenPanier: localStorage.getItem('cartToken') || null
            });

            const { token, refresh_token } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('refresh_token', refresh_token);

            const decoded = parseJwt(token);
            
            setIsLoggedIn(true);
            setUser(decoded);
            return { success: true, data: response.data };
        } catch (error) {
             if (error.response && error.response.data) {
                return { success: false, errors: error.response.data, message: error.response.message || 'Une erreur est survenue.' };
            }
            return { success: false, errors: { message: 'Une erreur est survenue.' } };
        }
    };
    const resetPassword = async ({password, confirmPassword, token}) => {
        try {
            const response = await axiosInstance.post('/reset-password', {
                token,
                password,
                confirmPassword,
            });
            return { success: true, data: response.data };
        } catch (error) {
            
            if (error.response && error.response.data) {
                // Return error details from backend (e.g., field errors)
                return { success: false, errors: error.response.data };
            }
            // Return generic error if no response data
            return { success: false, errors: { message: 'Une erreur est survenue.' } };
        }
    };
    const forgetPassword = async (email) => {
        try {
            const response = await axiosInstance.post('/request-reset-password', {
                email,
                webSite: 'https://lepharos.fr',
            });
            return { success: true, data: response.data };
        } catch (error) {
            if (error.response && error.response.data) {
                // Return error details from backend (e.g., field errors)
                return { success: false, errors: error.response.data };
            }
            // Return generic error if no response data
            return { success: false, errors: { message: 'Une erreur est survenue.' } };
        }
    };

    const checkAuth = async () => {
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refresh_token');

        const now = Date.now() / 1000;
        const decoded = token ? parseJwt(token) : null;

        // Si le token est encore valide (avec une marge de 60 secondes)
        if (token && decoded && decoded.exp && (decoded.exp > (now + 60))) {
            setIsLoggedIn(true);
            setUser(decoded);
            setAuthChecked(true);
            return;
        }

        // Si on a un refresh token, essayer de rafraîchir
        if (refreshToken && token) {
            try {
                // Utiliser axiosInstance qui a déjà la logique de refresh
                // Mais avec skipAuthRefresh pour éviter la boucle
                const res = await axiosInstance.post('/token/refresh', {
                    refresh_token: refreshToken
                }, {
                    headers: { Authorization: '' },
                    skipAuthRefresh: true
                });

                if (res.data && res.data.token) {
                    localStorage.setItem('token', res.data.token);
                    const refreshedDecoded = parseJwt(res.data.token);
                    setIsLoggedIn(true);
                    setUser(refreshedDecoded);
                    setAuthChecked(true);
                    return;
                }
            } catch (err) {
                // Si le refresh échoue, ne pas rediriger ici
                // Les interceptors axios géreront la redirection
            }
        }

        // Si aucun token valide, marquer comme non connecté
        setIsLoggedIn(false);
        setUser(null);
        setAuthChecked(true);
    };

    const register = async ({email, password, confirmPassword, firstname, lastname, phone}) => {
        try {
            const response = await axiosInstance.post('/register', {
                email,
                password,
                confirmPassword,
                firstname,
                lastname,
                phone,
            });
            return { success: true, data: response.data };
        } catch (error) {
            if (error.response && error.response.data) {
                // Return error details from backend (e.g., field errors)
                return { success: false, errors: error.response.data };
            }
            // Return generic error if no response data
            return { success: false, errors: { message: 'Une erreur est survenue.' } };
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    return (
        <AuthContext.Provider value={{ isLoggedIn, user, login, logout, register, forgetPassword, resetPassword, authChecked }}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook d'accès
export const useAuth = () => useContext(AuthContext);
